import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiMail, FiLock, FiEye, FiEyeOff } from 'react-icons/fi';
import { authAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';

export default function Login() {
  const [form, setForm]       = useState({ login: '', password: '' });
  const [show, setShow]       = useState(false);
  const [loading, setLoading] = useState(false);
  const { login }             = useAuth();
  const navigate              = useNavigate();

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.login || !form.password) return toast.error('Please fill in all fields.');
    setLoading(true);
    try {
      const res = await authAPI.login(form);
      login(res.data.token, res.data.user);
      toast.success('Welcome back, ' + res.data.user.full_name + '! 👋');
      navigate('/');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed. Try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="auth-page">
      {/* Left decorative panel */}
      <div className="auth-left">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'clamp(16px,3vw,28px)' }}>
          <div style={{ textAlign: 'center' }}>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(1.5rem,3.5vw,2.8rem)', background: 'linear-gradient(135deg,#E91E8C,#7B2FF7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', marginBottom: 12 }}>
              Share your world.
            </h2>
            <p style={{ color: 'var(--text-2)', fontSize: 'var(--fs-md)', maxWidth: 280 }}>
              Connect with friends, share moments, discover new stories.
            </p>
          </div>
          <div className="phone-mockup">
            <div className="phone-screen">
              <div style={{ padding: 'clamp(10px,2vw,16px)' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                  <img src="https://noris.in/wp-content/uploads/2023/01/logo.png" alt="Noris"
                    style={{ height: 'clamp(20px,3vw,28px)' }} onError={e => { e.target.style.display = 'none'; }} />
                </div>
                <div style={{ display: 'flex', gap: 8, marginBottom: 14, overflowX: 'hidden' }}>
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                      <div style={{ width: 'clamp(34px,6vw,44px)', height: 'clamp(34px,6vw,44px)', borderRadius: '50%', background: 'linear-gradient(135deg,#E91E8C,#7B2FF7)', padding: 2 }}>
                        <div style={{ width: '100%', height: '100%', borderRadius: '50%', background: `hsl(${i * 60},65%,68%)` }} />
                      </div>
                      <div style={{ width: 28, height: 6, background: '#eee', borderRadius: 3 }} />
                    </div>
                  ))}
                </div>
                {[1, 2].map(i => (
                  <div key={i} style={{ background: '#fff', border: '1px solid #eee', borderRadius: 10, marginBottom: 10, overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px' }}>
                      <div style={{ width: 26, height: 26, borderRadius: '50%', background: `hsl(${i * 120},55%,64%)`, flexShrink: 0 }} />
                      <div style={{ width: 70, height: 7, background: '#eee', borderRadius: 4 }} />
                    </div>
                    <div style={{ height: 'clamp(80px,14vw,130px)', background: `hsl(${i * 80},35%,86%)` }} />
                    <div style={{ padding: '8px 10px' }}>
                      <div style={{ width: '55%', height: 7, background: '#eee', borderRadius: 4 }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="auth-right">
        <div className="auth-box card fade-in">
          <div className="auth-logo">
            <img src="https://noris.in/wp-content/uploads/2023/01/logo.png" alt="Noris"
              onError={e => { e.target.style.display = 'none'; }} />
            <h1 className="gradient-text" style={{ fontFamily: 'Playfair Display, serif' }}>MSocial</h1>
            <p style={{ color: 'var(--text-2)', fontSize: 'var(--fs-sm)', marginTop: 4 }}>Share moments. Connect people.</p>
          </div>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
            <div className="input-group">
              <label>Email or Username</label>
              <div style={{ position: 'relative' }}>
                <FiMail style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }} />
                <input className="input" style={{ paddingLeft: 42 }} type="text" name="login"
                  placeholder="Enter email or username"
                  value={form.login} onChange={handleChange} autoComplete="username" />
              </div>
            </div>

            <div className="input-group">
              <label>Password</label>
              <div style={{ position: 'relative' }}>
                <FiLock style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none' }} />
                <input className="input" style={{ paddingLeft: 42, paddingRight: 44 }}
                  type={show ? 'text' : 'password'} name="password"
                  placeholder="Enter password"
                  value={form.password} onChange={handleChange} autoComplete="current-password" />
                <button type="button" onClick={() => setShow(p => !p)}
                  style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 4 }}>
                  {show ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                </button>
              </div>
            </div>

            <button type="submit" className="btn btn-primary w-full btn-lg" disabled={loading} style={{ marginTop: 4 }}>
              {loading
                ? <span className="spinner" style={{ borderTopColor: '#fff', borderColor: 'rgba(255,255,255,.3)' }} />
                : 'Log In'
              }
            </button>
          </form>

          <div className="auth-divider"><span>OR</span></div>

          <p style={{ textAlign: 'center', fontSize: 'var(--fs-sm)', color: 'var(--text-2)' }}>
            Don&apos;t have an account?{' '}
            <Link to="/register" style={{ color: 'var(--primary)', fontWeight: 700 }}>Sign up</Link>
          </p>
        </div>

        <p style={{ textAlign: 'center', fontSize: 'var(--fs-xs)', color: 'var(--text-3)', marginTop: 'var(--sp-3)' }}>
          © 2024 MSocial by{' '}
          <a href="https://noris.in" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>Noris Solutions</a>
        </p>
      </div>
    </div>
  );
}
